import { CHAIN_BY_ID, CHAINS } from 'consts';
import { ethereumUSDTAbi } from 'consts/abi/ethereumUSDTAbi';
import { TokenInfo } from 'types';
import { BN, bn } from 'utils/BigNumber';
import { Address, erc20Abi, getAddress } from 'viem';

export const DEFAULT_SIGNS = 6;

export const toSignificant = (
  amount: string | number | BN,
  decimals: number = 0,
  params?: {
    signs?: number;
    toFixed?: number;
    roundUp?: boolean;
  }
): string => {
  const { signs = DEFAULT_SIGNS, toFixed, roundUp = false } = params || {};

  const stringValue = amount.toString().replace(',', '.');
  const bnValue = bn(stringValue);

  if (bnValue.isNaN()) {
    console.warn(`Incorrect token amount: "${amount}"`);
    return amount.toString();
  }

  const ceilsLength = decimals
    ? bnValue.fromDecimals(decimals).toFixed(0).length
    : bnValue.toFixed(0).length;
  let decimalsLength = signs - ceilsLength > 0 ? signs - ceilsLength : 0;
  decimalsLength = toFixed ? Math.min(toFixed, decimalsLength) : decimalsLength;

  const BN_ROUND = roundUp ? bn.ROUND_UP : bn.ROUND_DOWN;
  const result = decimals
    ? bnValue.fromDecimals(decimals).toFixed(decimalsLength, BN_ROUND)
    : bnValue.toFixed(decimalsLength, BN_ROUND);

  return bn(result).toFixed();
};

export const addSeparators = (
  value: string | number | BN | null | undefined,
  separator = ' '
): string => {
  if (!value && value !== 0) return '';
  const stringValue = value.toString().replace(',', '.');

  if (bn(stringValue).isNaN()) {
    return value.toString();
  }

  const parts = stringValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return parts.join('.');
};

export const toDollars = (
  value: string | number | BN | null | undefined,
  params?: { withTilde?: boolean; separator?: string }
) => {
  const { withTilde = false, separator } = params || {};

  if (!value && value !== 0) return '';
  const stringValue = value.toString().replace(',', '.');
  const bnValue = bn(stringValue);

  if (bnValue.isNaN()) {
    return value.toString();
  }

  if (bnValue.eq(0)) return '$0.00';
  const rounded = bnValue.toFixed(2, bn.ROUND_DOWN);
  return `${withTilde ? '~' : ''}$${addSeparators(rounded, separator)}`;
};

export const getBalance = (
  balances: Record<Address, string | undefined>,
  tokenAddress: Address | null | undefined
) => {
  if (!tokenAddress) return undefined;
  return balances?.[tokenAddress];
};

export const cutAddress = (address: string, symbols: number = 4) => {
  try {
    const addr = getAddress(address);
    return `${addr.substring(0, symbols + 1)}...${addr.substring(addr.length - symbols)}`;
  } catch {
    console.error("Invalid input, address can't be parsed", address);
    return address;
  }
};

export const cutAnyAddress = (str: string, symbols: number = 4) => {
  if (str.length <= symbols * 2 + 3) return str;
  return `${str.substring(0, symbols)}...${str.substring(str.length - symbols)}`;
};

export const cutHash = (hash: string, symbols: number = 4) => {
  return `${hash.substring(0, symbols + 1)}...${hash.substring(hash.length - symbols)}`;
};

export const setPartialDataToObject = <T>(object: T, data: Partial<T>) => {
  let key: keyof T;
  for (key in data) {
    if (data[key] !== undefined) object[key] = data[key] as T[keyof T];
  }
};

export const isDefined = <T>(argument: T | undefined | null): argument is T => {
  return argument !== undefined && argument !== null;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getErc20Abi = (chainId: number, tokenAddress: string) => {
  return chainId === CHAINS.ethereum.id &&
    tokenAddress.toLowerCase() === '0xdac17f958d2ee523a2206206994597c13d831ec7'
    ? ethereumUSDTAbi
    : erc20Abi;
};

export const getScanUrl = (path: string, chainId: number) => {
  const chain = CHAIN_BY_ID[chainId];
  const scanUrl = chain?.blockExplorers?.default.url;
  if (!scanUrl) return '';
  const slash = path[0] === '/' ? '' : '/';
  return `${scanUrl}${slash}${path}`;
};

export const isValidSwapToken = (token: TokenInfo) => {
  return !(token.tags?.includes('hidden') && !token.tags?.includes('custom'));
};

export const cutTokenSymbol = (symbol: string | undefined, signs = 10) => {
  if (!symbol || symbol.length <= signs) return symbol;
  return symbol.slice(0, signs) + '...';
};

export const cutTokenName = (name: string | undefined, signs = 60) => {
  if (!name || name.length <= signs) return name;
  return name.slice(0, signs) + '...';
};

export const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const toShortNumber = (number: number) => {
  const ceil = number.toString().split('.')[0];
  const digits = ceil.length % 3 === 0 ? 0 : 1;
  const sign = number < 0 ? '-' : '';

  return (
    sign +
    Math.abs(number).toLocaleString('en-US', {
      maximumFractionDigits: digits,
      notation: Math.abs(number) >= 10_000 ? 'compact' : 'standard',
      compactDisplay: 'short',
    })
  );
};

export * from './formatNumberWithSubscriptZeros';
